import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../utils/axios';
import TsdPageBar from '../components/tsd/TsdPageBar';
import TsdScanCapture from '../components/tsd/TsdScanCapture';
import FulfillmentStatusChip from '../components/fulfillment/FulfillmentStatusChip';
import FulfillmentProductThumb from '../components/fulfillment/FulfillmentProductThumb';
import { getWbImageUrl, toThumbnail } from '../utils/productImage';
import {
  fulfillmentApiBase,
  fulfillmentTsdPath,
  normalizeFulfillmentMp,
  normalizePickingJobItem,
  pickingUsesTwoStepConfirm,
  withAccountBody,
  withAccountParams,
} from '../utils/fulfillmentApi.mjs';
import { isActivePickingStatus, pickingProgress } from './pickingUi.mjs';
import { pickingJobStatusInfo } from './fulfillmentUi.mjs';

function errorText(error) {
  const data = error?.response?.data || {};
  if (data.code === 'ms_out_of_stock') {
    return data.message || 'Недостаточно остатка МойСклад — подбор заблокирован';
  }
  if (data.code === 'ms_stock_unavailable') {
    return data.message || 'Остаток МойСклад сейчас недоступен. Проверьте настройки складов или креденшелы.';
  }
  return data.message || error.message || 'Не удалось выполнить операцию';
}

function settingsHint(message = '') {
  return /склад-источник|настройках кабинета|склады МойСклад/i.test(String(message || ''));
}

/** Нет связи МС↔МП: ведём в Настройки → Товары МС, после правки — «Обновить» в задании. */
function linkHint(message = '') {
  return /Товары МС/i.test(String(message || ''));
}

function warningSeverity(warning) {
  if (warning?.code === 'ms_product_unresolved' && /заблокирован/i.test(String(warning.message || ''))) return 'error';
  return 'warning';
}

function stockLabel(stock) {
  if (!stock) return null;
  if (!stock.known) {
    return stock.message || 'Остаток МС недоступен';
  }
  return `МС ${stock.ms_stock ?? 0} · резерв ${stock.reserved_total ?? 0} · доступно ${stock.available ?? 0}`;
}

/** Картинка: cards.image_url (product) → WB CDN; для Ozon WB CDN не используем. */
function ProductThumb({ target, marketplace }) {
  const preferred = target?.product?.image_url ? toThumbnail(target.product.image_url) : null;
  const fallback = (marketplace === 'ozon' || marketplace === 'yandex') ? null : getWbImageUrl(target?.task?.nm_id);
  const [src, setSrc] = useState(preferred || fallback);

  useEffect(() => {
    setSrc(preferred || fallback);
  }, [preferred, fallback]);

  if (!src) return null;
  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setSrc((current) => (fallback && current !== fallback ? fallback : null))}
      sx={{ width: 140, height: 180, objectFit: 'cover', borderRadius: 2, bgcolor: 'action.hover' }}
    />
  );
}

function canPickTarget(target) {
  if (!target?.stock) return true;
  // Явный запрет бэкенда (нет связи с МС при перемещении) — сильнее «остаток неизвестен».
  if (target.stock.can_pick === false) return false;
  if (target.stock.known === false) return true;
  return Number(target.stock.available) > 0;
}

function stockBlocked(stock) {
  if (!stock) return false;
  if (stock.can_pick === false) return true;
  return Boolean(stock.known) && Number(stock.available) <= 0;
}

function pickingItemLines(item) {
  const rows = item?.lines || item?.products;
  return Array.isArray(rows) && rows.length ? rows : null;
}

function compositionLines(item, marketplace) {
  if (marketplace === 'yandex') return pickingItemLines(item);
  const products = item?.task?.products_json || item?.posting?.products_json;
  if (!Array.isArray(products) || products.length < 2) return null;
  return products.map((product, index) => ({
    key: product.offer_id || product.sku || index,
    offer_id: product.offer_id || null,
    sku: product.sku || null,
    article: product.offer_id || null,
    name: product.name || null,
    barcode: product.barcode || null,
    quantity: product.quantity || 1,
    qty_need: product.quantity || 1,
    qty_picked: 0,
    status: item.status === 'picked' ? 'picked' : 'pending',
  }));
}

function lineCode(line) {
  return line?.offer_id || line?.article || line?.sku || 'Товар';
}

function lineName(line) {
  const name = String(line?.name || '').trim();
  const code = lineCode(line);
  if (!name || name === code) return '';
  return name;
}

export default function FulfillmentPickingJob() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectTimer = useRef(null);

  const marketplace = normalizeFulfillmentMp(
    searchParams.get('mp') || searchParams.get('marketplace') || 'wb',
  ) || 'wb';
  const accountId = searchParams.get('account_id') || searchParams.get('accountId') || '';
  const apiBase = fulfillmentApiBase(marketplace);
  const twoStep = pickingUsesTwoStepConfirm(marketplace);
  const isLocalWarehouse = marketplace === 'ozon' || marketplace === 'yandex';
  const listPath = fulfillmentTsdPath('/fulfillment/picking', { mp: marketplace, accountId });

  const [detail, setDetail] = useState(null);
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [blockedReason, setBlockedReason] = useState('');
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const lastScanRef = useRef(null);

  const goToListSoon = useCallback((message) => {
    setBlockedReason(message);
    setDetail(null);
    if (redirectTimer.current) window.clearTimeout(redirectTimer.current);
    redirectTimer.current = window.setTimeout(() => {
      navigate(listPath, { replace: true });
    }, 2500);
  }, [navigate, listPath]);

  useEffect(() => () => {
    if (redirectTimer.current) window.clearTimeout(redirectTimer.current);
  }, []);

  const loadDetail = useCallback(async () => {
    if (!jobId) {
      goToListSoon('Не указан номер задания');
      setLoading(false);
      return;
    }
    if (isLocalWarehouse && !accountId) {
      setError('В ссылке нет account_id — откройте задание из списка подбора');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${apiBase}/picking-jobs/${jobId}`, {
        params: withAccountParams(marketplace, accountId),
      });
      if (!isActivePickingStatus(data?.status)) {
        const status = pickingJobStatusInfo(data?.status);
        goToListSoon(
          `Задание ${data?.code || ''} уже ${status?.label?.toLowerCase() || 'закрыто'}. Возврат к списку…`,
        );
        return;
      }
      setBlockedReason('');
      setDetail(data);
    } catch (requestError) {
      const status = requestError?.response?.status;
      if (status === 404 || status === 403) {
        goToListSoon('Задание не найдено или назначено другому сборщику. Возврат к списку…');
      } else {
        setError(errorText(requestError));
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, goToListSoon, apiBase, marketplace, accountId, isLocalWarehouse]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const items = useMemo(
    () => (detail?.items || []).map((item) => normalizePickingJobItem(item, marketplace)),
    [detail, marketplace],
  );

  const resolve = async (raw) => {
    const code = String(raw || '').trim();
    if (!detail?.id || !code || resolving) return;
    if (!twoStep) {
      const last = lastScanRef.current;
      if (last && last.code === code && Date.now() - last.at < 1500) {
        setError('');
        setNotice('Повторный скан');
        return;
      }
    }
    setResolving(true);
    setError('');
    setNotice('');
    try {
      if (!twoStep) {
        const { data } = await axios.post(
          `${apiBase}/picking-jobs/${detail.id}/scan`,
          withAccountBody(marketplace, accountId || detail.account_id, { barcode: code }),
        );
        lastScanRef.current = { code, at: Date.now() };
        const remaining = Number(data?.remaining);
        if (!(Number.isFinite(remaining) && remaining <= 0)) {
          setNotice(data?.notice
            || (data?.posting_number ? `Подобрано ${data.posting_number}` : 'Товар отмечен как подобранный'));
        }
        await loadDetail();
        return;
      }

      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${detail.id}/scan/resolve`,
        withAccountBody(marketplace, accountId || detail.account_id, { barcode: code }),
      );
      setTarget(data?.target ? { ...data.target, scanned_barcode: code } : null);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setResolving(false);
    }
  };

  const closeConfirmation = () => {
    setTarget(null);
  };

  const confirm = async () => {
    if (!target || !detail) return;
    if (!canPickTarget(target)) {
      setError('Доступный остаток МойСклад равен нулю — подтвердить подбор нельзя');
      return;
    }
    setResolving(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${detail.id}/picks/confirm`,
        withAccountBody(marketplace, accountId || detail.account_id, {
          item_id: target.confirmation_key || target.id,
          barcode: target.scanned_barcode,
        }),
      );
      const remaining = Number(data?.remaining);
      if (!(Number.isFinite(remaining) && remaining <= 0) && data?.job_status !== 'completed') {
        setNotice('Товар отмечен как подобранный');
      }
      if (data?.stock_warning?.message) {
        setError(data.stock_warning.message);
      }
      closeConfirmation();
      if (data?.job_status === 'completed') {
        goToListSoon(`Задание ${detail.code || ''} закрыто. Возврат к списку…`);
      } else {
        await loadDetail();
      }
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setResolving(false);
    }
  };

  const closeJob = async ({ retry = false } = {}) => {
    if (!detail) return;
    setClosing(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${detail.id}/close`,
        withAccountBody(marketplace, accountId || detail.account_id, {}),
      );
      setNotice(retry
        ? 'Закрытие повторено'
        : `Задание ${data?.code || detail.code || ''} закрыто`);
      goToListSoon(`Задание ${data?.code || detail.code || ''} закрыто. Возврат к списку…`);
    } catch (requestError) {
      setError(errorText(requestError));
      await loadDetail().catch(() => {});
    } finally {
      setClosing(false);
    }
  };

  const progress = pickingProgress(detail || {});
  const active = detail && isActivePickingStatus(detail.status);
  const remainingCount = Math.max(0, progress.total - progress.picked);
  const allPicked = Boolean(active && progress.total > 0 && remainingCount === 0);
  const moveRetry = Boolean(detail?.ms_move_required && detail?.ms_move_status === 'error');
  const pickBlocked = target && !canPickTarget(target);
  const closePrompt = remainingCount === 0
    ? `Закрыть задание${detail?.code ? ` ${detail.code}` : ''}?${detail?.ms_move_required ? ' Будет создано перемещение МойСклад' : ''}`
    : `Не подобрано ${remainingCount} позиций — они вернутся в «Ожидают сборки». Закрыть?`;

  return (
    <>
      <TsdPageBar
        title={detail?.code || 'Подбор'}
        startAdornment={(
          <Tooltip title="К списку">
            <IconButton
              component={RouterLink}
              to={listPath}
              aria-label="К списку"
            >
              <BackIcon />
            </IconButton>
          </Tooltip>
        )}
      />
      <Stack
        spacing={2}
        sx={{
          maxWidth: 900,
          mx: 'auto',
          pb: 4,
          width: '100%',
          minWidth: 0,
          overflowX: 'hidden',
        }}
      >
        {blockedReason && <Alert severity="warning">{blockedReason}</Alert>}
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" icon={<CheckIcon />} onClose={() => setNotice('')}>{notice}</Alert>}

        {loading && <LinearProgress />}

        {!loading && !detail && !blockedReason && (
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1">Не удалось открыть задание</Typography>
            <Button component={RouterLink} to={listPath} sx={{ mt: 1 }} startIcon={<BackIcon />}>
              Назад к списку
            </Button>
          </Paper>
        )}

        {detail && (
          <>
            <Paper variant="outlined" sx={{ p: 2, overflow: 'hidden', minWidth: 0 }}>
              <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                  sx={{ minWidth: 0 }}
                >
                  <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                    {detail.code && detail.name && (
                      <Typography
                        variant="bodyMedium"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        {detail.name}
                      </Typography>
                    )}
                  </Box>
                  <FulfillmentStatusChip kind="pickingJob" status={detail.status} />
                </Stack>
                <LinearProgress variant="determinate" value={progress.percent} sx={{ height: 10, borderRadius: 5 }} />
                <Typography variant="bodyLarge">{progress.picked} из {progress.total} подобрано</Typography>
                {detail.stock_warnings?.map((warning) => {
                  let action;
                  if (settingsHint(warning.message)) {
                    action = (
                      <Button color="inherit" size="small" component={RouterLink} to="/settings/mp-accounts">
                        К настройкам
                      </Button>
                    );
                  } else if (linkHint(warning.message)) {
                    action = (
                      <Button color="inherit" size="small" component={RouterLink} to="/settings/ms-products">
                        К товарам МС
                      </Button>
                    );
                  }
                  return (
                    <Alert
                      key={`${warning.code}-${warning.message}`}
                      severity={warningSeverity(warning)}
                      action={action}
                      sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {warning.message}
                    </Alert>
                  );
                })}
                {allPicked && (
                  <Alert severity="success">Все позиции подобраны. Закройте задание</Alert>
                )}
                {moveRetry && (
                  <Alert severity="error">
                    Ошибка перемещения МС: {detail.ms_move_error}
                  </Alert>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                  {active && (
                    <Button
                      variant="contained"
                      color={moveRetry ? 'warning' : (allPicked ? 'success' : 'primary')}
                      onClick={() => setCloseConfirmOpen(true)}
                      disabled={closing || resolving}
                      sx={{
                        minHeight: allPicked ? 64 : 48,
                        width: allPicked ? '100%' : { xs: '100%', sm: 'auto' },
                        fontWeight: allPicked ? 700 : undefined,
                      }}
                    >
                      {closing
                        ? <CircularProgress size={22} color="inherit" />
                        : (moveRetry ? 'Повторить закрытие' : 'Закрыть задание')}
                    </Button>
                  )}
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadDetail}
                    disabled={loading || resolving || closing}
                    sx={{ minHeight: 48, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Обновить
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <TsdScanCapture
              disabled={!active || resolving}
              pause={Boolean(target)}
              onScan={resolve}
              helperText={active ? 'Сканируйте товар' : 'Это задание закрыто.'}
              dialogPlaceholder="Штрихкод товара"
            />

            <Stack spacing={1} sx={{ minWidth: 0 }}>
              {items.map((item) => {
                const lines = compositionLines(item, marketplace);
                const orderPicked = item.status === 'picked';
                return (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{ p: 1.5, opacity: orderPicked ? 0.65 : 1, overflow: 'hidden', minWidth: 0 }}
                >
                  <Stack spacing={1} sx={{ minWidth: 0 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      gap={1}
                      sx={{ minWidth: 0 }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        {!lines && (
                          <FulfillmentProductThumb
                            product={item.product}
                            task={item.task}
                            marketplace={marketplace}
                            size={48}
                          />
                        )}
                        <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                          <Typography
                            variant="bodyLarge"
                            sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          >
                            {lines
                              ? `Заказ ${item.task?.wb_order_id || item.task?.posting_number || ''}`
                              : (item.task?.offer_id || item.product?.article || item.task?.article || `nmID ${item.task?.nm_id}`)}
                          </Typography>
                          {!lines && (
                            <Typography
                              variant="bodySmall"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                            >
                              ШК {item.task?.barcode || item.product?.barcode || '—'} · заказ {item.task?.wb_order_id || item.task?.posting_number}
                            </Typography>
                          )}
                          {stockLabel(item.stock) && (
                            <Typography
                              variant="bodySmall"
                              color={stockBlocked(item.stock) ? 'error.main' : 'text.secondary'}
                              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                            >
                              {stockLabel(item.stock)}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      <FulfillmentStatusChip
                        code={orderPicked ? 'picked' : 'pending'}
                        label={orderPicked ? 'Подобран' : 'Ожидает'}
                      />
                    </Stack>
                    {lines && lines.map((line) => {
                      const linePicked = line.status === 'picked'
                        || Number(line.qty_picked) >= Number(line.qty_need || line.quantity || 1);
                      const need = Number(line.qty_need || line.quantity || 1);
                      return (
                        <Stack
                          key={line.key || line.offer_id || line.sku || line.article}
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ minWidth: 0, pl: { sm: 0.5 } }}
                        >
                          <FulfillmentProductThumb
                            product={line}
                            marketplace={marketplace}
                            size={40}
                          />
                          <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                            <Typography
                              variant="bodyMedium"
                              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                            >
                              {lineCode(line)}
                            </Typography>
                            {lineName(line) ? (
                              <Typography
                                variant="bodySmall"
                                color="text.secondary"
                                sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                              >
                                {lineName(line)}
                              </Typography>
                            ) : null}
                            <Typography variant="bodySmall" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                              ШК {line.barcode || '—'}
                              {line.sku && String(line.sku) !== String(lineCode(line)) ? ` · SKU ${line.sku}` : ''}
                              {` · ${need} шт`}
                              {Number(line.qty_picked) > 0 && !linePicked
                                ? ` · ${line.qty_picked} из ${need}`
                                : ''}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            color={linePicked ? 'success' : 'default'}
                            variant={linePicked ? 'filled' : 'outlined'}
                            label={linePicked ? 'подобран' : 'ожидает'}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </Paper>
                );
              })}
            </Stack>
          </>
        )}
      </Stack>

      <Dialog
        open={closeConfirmOpen}
        onClose={() => { if (!closing) setCloseConfirmOpen(false); }}
        disableAutoFocus
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Закрыть задание?</DialogTitle>
        <DialogContent>
          <Typography variant="bodyLarge">{closePrompt}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setCloseConfirmOpen(false)}
            disabled={closing}
            size="large"
            sx={{ minHeight: 52, flex: 1 }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            color={remainingCount > 0 ? 'warning' : 'primary'}
            onClick={() => {
              setCloseConfirmOpen(false);
              closeJob({ retry: moveRetry });
            }}
            disabled={closing}
            size="large"
            sx={{ minHeight: 52, flex: 1 }}
          >
            {closing ? <CircularProgress size={22} color="inherit" /> : 'Закрыть задание'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(target)} onClose={closeConfirmation} disableAutoFocus fullWidth maxWidth="xs">
        <DialogTitle>Подтвердить подбор?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            {target && <ProductThumb target={target} marketplace={marketplace} />}
            <Typography
              variant="titleLarge"
              textAlign="center"
              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere', px: 1 }}
            >
              {target?.product?.article || target?.task?.article || `nmID ${target?.task?.nm_id}`}
            </Typography>
            <Typography variant="bodyLarge" sx={{ wordBreak: 'break-all', textAlign: 'center' }}>
              Штрихкод: <strong>{target?.task?.barcode}</strong>
            </Typography>
            <Typography
              variant="bodyMedium"
              color="text.secondary"
              sx={{ wordBreak: 'break-all', textAlign: 'center' }}
            >
              nmID {target?.task?.nm_id} · заказ {target?.task?.wb_order_id}
            </Typography>
            {stockLabel(target?.stock) && (
              <Typography
                variant="bodyMedium"
                color={pickBlocked ? 'error.main' : 'text.secondary'}
                textAlign="center"
              >
                {stockLabel(target.stock)}
              </Typography>
            )}
            {pickBlocked && (
              <Alert severity="error" sx={{ width: '100%' }}>
                Доступный остаток равен нулю — подтверждение заблокировано.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeConfirmation} size="large" sx={{ minHeight: 52, flex: 1 }}>Отмена</Button>
          <Button
            variant="contained"
            onClick={confirm}
            disabled={resolving || pickBlocked}
            size="large"
            sx={{ minHeight: 52, flex: 1 }}
          >
            {resolving ? <CircularProgress size={22} color="inherit" /> : 'Подтвердить'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
